"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.processSocialMutations = processSocialMutations;
var _rdflib = require("rdflib");
var _solidUi = require("solid-ui");
var _idNodeFactory = require("../shared/idNodeFactory");
var _rdfMutationHelpers = require("../shared/rdfMutationHelpers");
var _texts = require("../../texts");
var _helpers = require("./helpers");
var _selectors = require("./selectors");
var _rdfList = require("../shared/rdfList");
var _debug = require("../../utils/debug");
/* The code below for handling reordering were generated by AI Model: GPT-5.3-Codex. */
/* Prompt: can you implement on SocialEditDialog add the bentoIcon as the first item 
on the row, you should be able to drag this button around and it should allow you 
to change the order of the rows and that should change the order in the mutations 
that happen for this data. */

const SOCIAL_ONTOLOGY_NS = 'https://solidos.github.io/profile-pane/src/ontology/socialMedia.ttl#';
function ensureSocialPrefix(store) {
  (0, _rdfMutationHelpers.registerStorePrefix)(store, 'soc', SOCIAL_ONTOLOGY_NS);
}
function nodeKey(node) {
  return `${node.termType}:${node.value}`;
}
function toObjectNode(value) {
  const normalized = (value || '').trim();
  if (!normalized) return null;
  if (normalized.startsWith('http://') || normalized.startsWith('https://')) {
    return (0, _rdflib.sym)(normalized);
  }
  return (0, _rdflib.literal)(normalized);
}
function normalizeValue(value) {
  if (typeof value === 'string') return value.trim();
  if (value === null || value === undefined) return '';
  return String(value).trim();
}
function toAccountNameFromHomepage(homepage, profilePrefix) {
  const homepageValue = normalizeValue(homepage);
  if (!homepageValue) return '';
  const prefixValue = normalizeValue(profilePrefix);
  if (prefixValue) {
    let trimmed = homepageValue;
    const lowerPrefix = prefixValue.toLowerCase();
    while (trimmed.toLowerCase().startsWith(lowerPrefix)) {
      trimmed = trimmed.slice(prefixValue.length);
    }
    if (trimmed) return trimmed;
    try {
      const homepageUrl = new URL(homepageValue);
      const prefixUrl = new URL(prefixValue);
      const normalizeHost = host => host.toLowerCase().replace(/^www\./, '');
      const homepageHost = normalizeHost(homepageUrl.hostname);
      const prefixHost = normalizeHost(prefixUrl.hostname);
      const prefixPath = (prefixUrl.pathname || '/').replace(/\/+$/, '');
      const homepagePath = homepageUrl.pathname || '/';
      if (homepageHost === prefixHost && homepagePath.startsWith(prefixPath)) {
        const remainder = homepagePath.slice(prefixPath.length).replace(/^\/+/, '');
        if (remainder) return remainder;
      }
    } catch {
      // Ignore URL parsing issues and continue to generic normalization.
    }
  }
  return homepageValue.replace(/^https?:\/\//i, '').replace(/^www\./i, '');
}
function buildSocialStatements(doc, node, row, accountOption) {
  const inserts = [];
  if (accountOption?.classUri) {
    inserts.push((0, _rdflib.st)(node, _solidUi.ns.rdf('type'), (0, _rdflib.sym)(accountOption.classUri), doc));
  }
  const normalizedName = normalizeValue(row.name);
  const isOther = normalizedName.toLowerCase() === 'other' || accountOption?.classUri?.endsWith('#OtherAccount');
  if (isOther) {
    const homepageNode = toObjectNode(row.homepage);
    const iconNode = toObjectNode(row.icon);
    if (homepageNode) inserts.push((0, _rdflib.st)(node, _solidUi.ns.foaf('homepage'), homepageNode, doc));
    if (iconNode) inserts.push((0, _rdflib.st)(node, _solidUi.ns.foaf('icon'), iconNode, doc));
    if (normalizedName && normalizedName.toLowerCase() !== 'other') {
      inserts.push((0, _rdflib.st)(node, _solidUi.ns.rdfs('label'), (0, _rdflib.literal)(normalizedName), doc));
    }
    return inserts;
  }
  const accountPrefix = accountOption?.userProfilePrefix || accountOption?.homepage;
  const accountName = toAccountNameFromHomepage(normalizeValue(row.homepage), accountPrefix);
  if (accountName) {
    inserts.push((0, _rdflib.st)(node, _solidUi.ns.foaf('accountName'), (0, _rdflib.literal)(accountName), doc));
  }
  return inserts;
}
function collectListChainNodes(store, listHead, doc) {
  if (!store.any(listHead, _solidUi.ns.rdf('first'), null, doc)) return [];
  const visited = new Set();
  const nodes = [];
  let current = listHead;
  while (current) {
    const key = `${current.termType}:${current.value}`;
    if (visited.has(key)) break;
    visited.add(key);
    nodes.push(current);
    const rest = store.any(current, _solidUi.ns.rdf('rest'), null, doc);
    if (!rest || rest.termType === 'NamedNode' && rest.value === _solidUi.ns.rdf('nil').value) {
      break;
    }
    current = rest;
  }
  return nodes;
}
function mergeSocialOps(existingRows, socialOps) {
  const removeKeys = new Set(socialOps.remove.map(row => normalizeValue(row.entryNode)).filter(Boolean));
  const updateByKey = new Map(socialOps.update.map(row => [normalizeValue(row.entryNode), row]).filter(([key]) => Boolean(key)));
  const mergedExisting = existingRows.filter(row => !removeKeys.has(normalizeValue(row.entryNode))).map(row => {
    const key = normalizeValue(row.entryNode);
    return updateByKey.get(key) || row;
  });
  const creates = socialOps.create.filter(row => {
    return Boolean(normalizeValue(row.name) || normalizeValue(row.homepage) || normalizeValue(row.icon));
  });
  return [...mergedExisting, ...creates];
}
function rowsFromOrderedInput(orderedRows) {
  return (orderedRows || []).filter(row => row.status !== 'deleted').filter(row => Boolean(normalizeValue(row.name) || normalizeValue(row.homepage) || normalizeValue(row.icon) || normalizeValue(row.entryNode))).map(row => ({
    name: normalizeValue(row.name),
    icon: normalizeValue(row.icon),
    homepage: normalizeValue(row.homepage),
    entryNode: normalizeValue(row.entryNode),
    status: row.status
  }));
}
async function mutateSocialEntries(store, subject, socialOps, orderedRows) {
  ensureSocialPrefix(store);
  const doc = subject.doc();
  const accountOptions = (0, _helpers.getSocialAccountOptions)(store);
  const optionForRow = row => (0, _helpers.findSocialAccountOption)(accountOptions, normalizeValue(row.name));
  const existingRows = (0, _selectors.presentSocial)(subject, store).accounts.map(account => ({
    name: normalizeValue(account.name),
    icon: normalizeValue(account.icon),
    homepage: normalizeValue(account.homepage),
    entryNode: account.entryNode.value,
    status: 'existing'
  }));
  const nextRows = orderedRows && orderedRows.length ? rowsFromOrderedInput(orderedRows) : mergeSocialOps(existingRows, socialOps);
  const canForcePut = Boolean(store.updater?.serialize && store.fetcher?.webOperation);
  const accountObjects = store.each(subject, _solidUi.ns.foaf('account'), null, doc);
  const existingListNodes = (0, _rdfMutationHelpers.uniqueStatements)(accountObjects.flatMap(objectNode => {
    return collectListChainNodes(store, objectNode, doc).flatMap(node => store.statementsMatching(node, null, null, doc));
  }));
  const existingAccountNodes = Array.from(new Map(accountObjects.flatMap(objectNode => (0, _rdfList.expandRdfList)(store, objectNode)).filter(node => node.termType === 'NamedNode').map(node => [`${node.termType}:${node.value}`, node])).values());
  const rowEntryNodes = nextRows.map(row => {
    if (normalizeValue(row.entryNode)) {
      const existing = (0, _rdfMutationHelpers.findExistingNode)(existingAccountNodes, row.entryNode);
      if (existing && existing.termType === 'NamedNode') return existing;
    }
    return (0, _idNodeFactory.createIdNode)(doc);
  });
  const retainedAccountNodeKeys = new Set(rowEntryNodes.map(node => nodeKey(node)));
  const retainedAccountNodes = existingAccountNodes.filter(node => retainedAccountNodeKeys.has(nodeKey(node)));
  const removedAccountNodes = existingAccountNodes.filter(node => !retainedAccountNodeKeys.has(nodeKey(node)));
  const insertions = [];
  if (rowEntryNodes.length > 0) {
    insertions.push((0, _rdflib.st)(subject, _solidUi.ns.foaf('account'), new _rdflib.Collection(rowEntryNodes), doc));
  }
  nextRows.forEach((row, index) => {
    insertions.push(...buildSocialStatements(doc, rowEntryNodes[index], row, optionForRow(row)));
  });
  const deletions = (0, _rdfMutationHelpers.uniqueStatements)([...store.statementsMatching(subject, _solidUi.ns.foaf('account'), null, doc), ...existingListNodes, ...removedAccountNodes.flatMap(node => (0, _rdfMutationHelpers.collectNodeStatements)(store, node, doc)), ...retainedAccountNodes.flatMap(node => [...store.statementsMatching(node, _solidUi.ns.rdf('type'), null, doc), ...store.statementsMatching(node, _solidUi.ns.foaf('accountName'), null, doc), ...store.statementsMatching(node, _solidUi.ns.foaf('homepage'), null, doc), ...store.statementsMatching(node, _solidUi.ns.foaf('icon'), null, doc), ...store.statementsMatching(node, _solidUi.ns.rdfs('label'), null, doc)])]);
  await (0, _rdfMutationHelpers.runUpdateTransport)(store, doc, deletions, (0, _rdfMutationHelpers.uniqueStatements)(insertions), {
    unsupportedMessage: 'Social updates are not supported by this store updater.',
    failureMessage: 'Failed to save updates',
    forcePut: canForcePut,
    useDavFallback: !canForcePut,
    usePutFallback: true
  });
}
async function processSocialMutations(store, subject, mutationPlan, orderedRows) {
  try {
    await mutateSocialEntries(store, subject, mutationPlan, orderedRows);
  } catch (error) {
    const rootError = error instanceof Error ? error : new Error(String(error));
    (0, _debug.error)(_texts.socialMutationSaveFailedDebugText, rootError);
    throw new Error(_texts.saveSocialUpdatesFailedMessageText, {
      cause: rootError
    });
  }
}